import { createContext, useEffect, useRef } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import { Authenticator, View } from '@aws-amplify/ui-react';
import { awsConfig } from '../config/aws-exports';
import { I18n } from 'aws-amplify';
import i18next from 'i18next'; // Import the i18next instance
import Icon from '../components/Icon/Icon';
import { useCookies } from 'react-cookie';
import { AFFILIATION_KEY, USER_ROLE_KEY, flushPendingLoginEvent, queueLoginEvent, updateConsent } from '../utils/trackingGA4';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import './AuthContext.scss';
import { isMarket } from '../utils/markets';
import { Markets, Role } from '../types';
import { parseBool, parseJsonList } from '../utils';
import useAuthStore from '../store/AuthStore';
import { UserGroup } from 'src/api/models/Enums';
import { RoleEnum } from 'src/api';

Amplify.configure(awsConfig);
const AuthContext = createContext(null);

let authScreenLabels;
if (isMarket(Markets.it)) {
	authScreenLabels = {
		it: {
			'Attempt limit exceeded, please try after some time.':
				"Limite di tentativi superato, riprova dopo un po' di tempo.",
			Sending: 'Caricamento',
			Code: 'Codice',
			'Code *': 'Codice *',
			'New Password': 'Nuova password',
			'Username cannot be empty': 'Il nome utente non può essere vuoto',
			'Confirm Password': 'Conferma password',
			'Reset Password': 'Ripristina la password',
			'Enter your username': "Inserisci l'email",
			'Enter your Username': 'Email',
			'Enter your Password': 'Password',
			'Resend Code': 'Rimanda il codice',
			Submit: 'Invia',
			'Send code': 'Invia',
			'Back to Sign In': 'Torna',
			Username: "Inserisci l'email",
			Password: 'Inserisci la password',
			'Forgot your password?': 'Hai dimenticato la password?',
			'Sign in': 'Accedi',
			'Account recovery requires verified contact information':
				"Il recupero dell'account richiede informazioni di contatto verificate",
			Verify: 'Verificare',
			Skip: 'Salta',
			'Signing in': 'Accedi',
			'Incorrect username or password.': 'Nome utente o password errati.',
		},
	};
} else if (isMarket(Markets.es)) {
	authScreenLabels = {
		it: {
			'Attempt limit exceeded, please try after some time.':
				'Límite de intentos superado, intenta de nuevo después de un tiempo.',
			Sending: 'Cargando',
			Code: 'Código',
			'Code *': 'Código *',
			'New Password': 'Nueva contraseña',
			'Username cannot be empty': 'El nombre de usuario no puede estar vacío',
			'Confirm Password': 'Confirmar contraseña',
			'Reset Password': 'Restablecer la contraseña',
			'Enter your username': 'Ingresa tu correo electrónico',
			'Enter your Username': 'Correo electrónico',
			'Enter your Password': 'Contraseña',
			'Resend Code': 'Reenviar código',
			Submit: 'Enviar',
			'Send code': 'Enviar',
			'Back to Sign In': 'Volver',
			Username: 'Ingresa tu correo electrónico',
			Password: 'Ingresa tu contraseña',
			'Forgot your password?': '¿Olvidaste tu contraseña?',
			'Sign in': 'Iniciar sesión',
			'Account recovery requires verified contact information':
				'La recuperación de la cuenta requiere información de contacto verificada',
			Verify: 'Verificar',
			Skip: 'Saltar',
			'Signing in': 'Iniciando sesión',
			'Incorrect username or password.': 'Usuario o contraseña incorrectos.',
		},
	};
}

I18n.setLanguage('it');
I18n.putVocabularies(authScreenLabels);

/**
 * Scrive su cookie/localStorage l'affiliation e il ruolo dell'utente, in modo
 * che getUserAffiliation() abbia sempre un fallback aggiornato.
 */
const syncAuthAttributesToStorage = (
	attributes: Record<string, any> | undefined,
	setCookie: (name: string, value: any, options?: any) => void,
	removeCookie: (name: string, options?: any) => void
) => {
	const expirationDate = new Date();
	expirationDate.setMonth(expirationDate.getMonth() + 26);
	const cookieOptions = { expires: expirationDate, path: '/' };

	const affiliation = parseJsonList(attributes?.['custom:reference_partner'])[0];
	if (affiliation) {
		setCookie(AFFILIATION_KEY, affiliation, cookieOptions);
		localStorage.setItem(AFFILIATION_KEY, affiliation);
	} else {
		removeCookie(AFFILIATION_KEY, { path: '/' });
		localStorage.removeItem(AFFILIATION_KEY);
	}

	const userRole = attributes?.['custom:role'];
	if (userRole) {
		setCookie(USER_ROLE_KEY, userRole, cookieOptions);
	} else {
		removeCookie(USER_ROLE_KEY, { path: '/' });
	}
};

const formFields = {
	signIn: {
		username: {
			label: '',
		},
		password: { label: '' },
	},
	resetPassword: {
		username: {
			label: '',
		},
	},
	confirmResetPassword: {
		confirmation_code: {
			label: '',
		},
		password: {
			label: '',
		},
		confirm_password: {
			label: '',
		},
	},
};

const components: any = {
	Header() {
		return (
			<View className="icon-view" textAlign="center">
				<Icon name="horizon-light" />
			</View>
		);
	},
	Footer() {
		const { t } = useTranslation();

		return (
			<View className="footer small text-white" textAlign="center">
				<div>
					<strong>Horizon Automotive SpA</strong>
				</div>
				<div>{t('business_fiscal_code').toUpperCase()}: IT11133990967</div>
				<div className="mt-3">
					<strong>{t('headquarter')} Milano:</strong> Via del Bosco Rinnovato,
					8/Palazzo U4, 20057, Milano
				</div>
				<div>
					<strong>{t('branch')} Bergamo:</strong> Via per Grumello 43/A, 24127,
					Bergamo
				</div>
				<div>
					<strong>{t('branch')} Padova:</strong> Viale della Navigazione Interna
					71, 35129 Padova
				</div>
				<div>
					<strong>{t('branch')} Torino:</strong> Corso Susa 242, 10098, Rivoli
				</div>
				<div>
					<strong>{t('branch')} San Benedetto del Tronto:</strong> Via Pomezia
					3, 63074, San Benedetto del Tronto
				</div>
			</View>
		);
	},
};

/**
 * Tiene allineato l'utente nello store usato da GTMsendEvent.
 */
const AuthStoreSync = ({ user }) => {
	const setUser = useAuthStore((state) => state.setUser);

	useEffect(() => {
		if (useAuthStore.getState().user !== user) {
			setUser(user);
		}
	}, [user, setUser]);

	return null;
};

const AuthProvider = ({ children }) => {
	const [cookies, setCookie, removeCookie] = useCookies();
	const attributedFetched = useRef(false);
	const setUser = useAuthStore((state) => state.setUser);

	let rowConsent: any = null;
	try {
		rowConsent = localStorage.getItem('tracking_consent');
	} catch (e) {
		rowConsent = null;
	}
	const consent = rowConsent && JSON.parse(rowConsent);

	const services = {
		async handleSignIn(formData) {
			let { username, password } = formData;

			const signIn = await Auth.signIn({
				username,
				password,
			});

			const expirationDate = new Date();
			setUser(signIn);

			expirationDate.setMonth(expirationDate.getMonth() + 26);
			setCookie('userID', signIn?.attributes?.sub, { expires: expirationDate });
			localStorage.setItem('userID', signIn?.attributes?.sub);
			localStorage.setItem('username', signIn?.username);

			syncAuthAttributesToStorage(signIn?.attributes, setCookie, removeCookie);

			const rawIsFirstLogin = localStorage.getItem('isFirstLogin');
			let isFirstLogin;
			if (rawIsFirstLogin !== null) {
				isFirstLogin = JSON.parse(rawIsFirstLogin);
			} else {
				// first login
				localStorage.setItem('isFirstLogin', JSON.stringify(true));
			}

			!isFirstLogin && queueLoginEvent(signIn?.attributes?.['custom:role']);

			// challangeName can be NEW_PASSWORD_REQUIRED upon first login after account creation
			if (!signIn.challengeName) {
				window.location.reload();
			} else {
				flushPendingLoginEvent();
			}

			return signIn;
		},
	};

	return (
		<Authenticator
			hideSignUp={true}
			formFields={formFields}
			components={components}
			services={services}
		>
			{({ user, signOut }) => {
				let businessName: string | object =
					user?.attributes?.['custom:business_name'] ?? '';
				try {
					if (typeof businessName === 'string' && businessName.trim() !== '') {
						businessName = JSON.parse(businessName);
					}
				} catch (error) {
					console.warn(
						'Failed to parse custom:business_name, using as string:',
						businessName
					);
				}
				const userBusinessName = businessName;
				const userGroup = user?.attributes?.['custom:group'];
				const visibilityTag = user?.attributes?.['custom:visibility_tag'];
				const isSalesMaserati = visibilityTag?.includes('MASERATI') || false;
				const isSalesVolkswagen = visibilityTag?.includes('VOLKSWAGEN') || false;
				const isSalesStellantis = visibilityTag?.includes('STELLANTIS') || false;
				const isSalesStellantisAndYou = visibilityTag?.includes('STELLANTIS_YOU') || false;
				const isSalesFacile = visibilityTag?.includes('FACILE') || false;
				const userRole = user?.attributes?.['custom:role'] as Role;
				const canViewPromoNote = ![RoleEnum.FLEET_MANAGER, RoleEnum.SEGNALATORE].includes(userRole as RoleEnum) && !isSalesStellantis && !isSalesStellantisAndYou && !isSalesFacile;

				const value: any = {
					user,
					signOut,
					userRole,
					userVisibilityTag: JSON.parse(
						user?.attributes?.['custom:visibility_tag'] || '[]'
					),
					userDisplayCommissions: JSON.parse(
						user?.attributes?.['custom:display_commissions'] || '[]'
					),
					userBusinessName: userBusinessName,
					userReferenceBackoffice:
						user?.attributes?.['custom:reference_backoffice'],
					userReferencePartner:
						user?.attributes?.['custom:reference_partner'],
					userCanReassignLead: parseBool(user?.attributes?.['custom:can_reassign_leads'] || ""),
					userReferencePartnerList: parseJsonList(user?.attributes?.['custom:reference_partner']),
					userIsSalesforceSales: parseBool(user?.attributes?.['custom:is_salesforce_sales'] || ""),
					isSalesMaserati,
					isSalesVolkswagen,
					isSalesStellantis,
					isSalesFacile,
					isSalesStellantisAndYou,
					isBrandedHmp: isSalesMaserati || isSalesVolkswagen || isSalesStellantis || isSalesFacile,
					userProvvigioniSegnalatore: JSON.parse(
						user?.attributes?.['custom:provv_segnalatore'] || '0'
					),
					userReferenceSalesDealer: user?.attributes?.['custom:ref_salesdealer'] || null,
					canApproveSegnalatori: userGroup?.toUpperCase()?.includes(UserGroup.CAN_APPROVE_SEGNALATORI) ?? false,
					canViewPromoNote,
				};

				// refetch user attributes only once because amplify auth does cache the attributes and returns the cached version
				// https://github.com/aws-amplify/amplify-js/issues/5043
				if (!attributedFetched.current) {
					attributedFetched.current = true;

					Auth.currentAuthenticatedUser({ bypassCache: true })
						.then((freshUser) => {
							if (freshUser?.attributes) {
								setUser(freshUser);
								syncAuthAttributesToStorage(freshUser.attributes, setCookie, removeCookie);
							}
						})
						.catch((error) => {
							console.warn('Warning fetching user attributes:', error);
						});
				}

				// set consent from previous user choice
				localStorage.setItem('userRole', JSON.stringify(value.userRole));

				if (user) {
					Sentry.setUser({
						email: user.attributes?.email,
						id: value.userRole + ':' + user.attributes?.email,
					});
				}

				consent && updateConsent();
				return (
					<AuthContext.Provider value={value}>
						<AuthStoreSync user={user} />
						{children}
					</AuthContext.Provider>
				);
			}}
		</Authenticator>
	);
};

export { AuthContext, AuthProvider };
